// Ecwid storefront script lifecycle + bag handoff.
// The script is preloaded app-wide (deferred — nothing renders). The
// ProductBrowser widget is initialized ONLY when the /checkout page mounts,
// directly into its inline slot, so checkout scrolls natively with the page.

const STORE_ID = process.env.REACT_APP_ECWID_STORE_ID;
export const WIDGET_ID = `ecwid-product-browser-${STORE_ID}`;

export const ensureEcwidScript = () => {
  if (window.ecwidReady || !STORE_ID) return window.ecwidReady;
  window.ecwidReady = new Promise((resolve, reject) => {
    window.ecwid_script_defer = true;
    window._xnext_initialization_scripts = [
      { widgetType: "ProductBrowser", id: WIDGET_ID, arg: [`id=${WIDGET_ID}`] },
    ];
    const script = document.createElement("script");
    script.id = "ecwid-script";
    script.async = true;
    script.charset = "utf-8";
    script.src = `https://app.ecwid.com/script.js?${STORE_ID}&data_platform=code`;
    script.onload = () => resolve(window.Ecwid);
    script.onerror = () => reject(new Error("Ecwid script failed to load"));
    document.head.appendChild(script);
  });
  window.ecwidReady.catch(() => {});
  return window.ecwidReady;
};

// Wait for the Ecwid JS API, then initialize the widget into its container.
export const initEcwidWidget = async () => {
  await ensureEcwidScript();
  await new Promise((resolve, reject) => {
    if (window.Ecwid?.Cart) return resolve();
    window.Ecwid?.OnAPILoaded?.add?.(resolve);
    let tries = 0;
    const poll = setInterval(() => {
      if (window.Ecwid?.Cart) {
        clearInterval(poll);
        resolve();
      } else if (++tries > 150) {
        clearInterval(poll);
        reject(new Error("Secure checkout is unavailable right now"));
      }
    }, 200);
  });
  window.Ecwid.init();
  return window.Ecwid;
};

const getCart = (ecwid) => new Promise((resolve) => ecwid.Cart.get((c) => resolve(c)));

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Ecwid's cart callbacks usually fire but are not guaranteed to; never hang on
// them. Fire the op, wait for the callback OR a timeout, then verify state
// once at the end. Polling during a mutation triggers Ecwid-internal query
// cancellations, so we keep Cart.get calls to a minimum with settle delays.
const OPS_TIMEOUT = 12000;

const callEcwid = (fn) =>
  new Promise((resolve) => {
    let settled = false;
    const done = (payload) => {
      if (!settled) {
        settled = true;
        resolve(payload);
      }
    };
    try {
      fn(done);
    } catch {
      done(null);
    }
    setTimeout(() => done(null), OPS_TIMEOUT);
  });

const addLine = (ecwid, line) => {
  const payload = { id: Number(line.productId), quantity: Number(line.quantity) };
  if (line.options && Object.keys(line.options).length) payload.options = line.options;
  return callEcwid((done) => ecwid.Cart.addProduct(payload, (ok, _p, _c, err) => done({ ok, err })));
};

const cartHas = (cart, line) =>
  (cart?.items || []).some((i) => Number(i.product?.id ?? i.productId) === Number(line.productId));

// Clears the Ecwid cart, re-adds every line (with Size/Color options), then
// drives the widget to the cart step. Returns a warning string listing any
// lines that could not be confirmed in the final cart.
export const handoffToEcwid = async (ecwid, lines) => {
  if (!lines.length) return null;
  await callEcwid((done) => ecwid.Cart.clear(() => done(true)));
  await sleep(500);
  for (const line of lines) {
    await addLine(ecwid, line);
    await sleep(500);
  }
  // Single verification pass + one retry for any line that didn't land
  let cart = await getCart(ecwid);
  for (const line of lines) {
    if (!cartHas(cart, line)) {
      await addLine(ecwid, line);
      await sleep(600);
      cart = await getCart(ecwid);
    }
  }
  const failed = lines.filter((l) => !cartHas(cart, l)).map((l) => l.name || l.productId);

  // The hashbang is the widget's native routing; openPage covers the live case.
  if (!window.location.hash.includes("~/cart")) {
    window.location.hash = "!/~/cart";
  }
  try {
    ecwid.openPage("cart");
  } catch {
    /* hashbang above already routes the widget */
  }

  return failed.length ? `Couldn't add: ${failed.join(", ")} — your bag below may be missing items.` : null;
};
